/* ------------------------------------------------------------------
 * cart-drawer-analytics.js
 *
 * Instruments the Stiletto quick-cart (slide-in cart drawer) with
 * dual-pipe analytics (window.dataLayer + Shopify.analytics.publish).
 * Same pattern as mobile-nav.liquid + search-modal.liquid +
 * home-analytics.js.
 *
 * Events emitted:
 *   - cart_drawer_open   { source, cart_item_count, cart_value }
 *   - cart_drawer_close  { dwell_ms, close_source, did_checkout }
 *
 * Source inference — we hook the events that lead to a drawer-open:
 *   - click on [data-js-cart-icon]                     → "header_cart_icon"
 *   - form submit inside a product form (ATC button)   → "atc_button"
 *   - any other trigger (programmatic, app-driven)     → "unknown"
 *
 * State tracking: the drawer's `.quick-cart__wrapper` toggles the
 * `.active` class on open / close. We MutationObserver that.
 *
 * Cart total/count — read from the live cart state. Stiletto sets
 * window.theme.cart at boot; we also fall back to scraping
 * .quick-cart__heading sup (item count) and parsing the visible
 * subtotal element. As a safety net we also fetch /cart.js if needed.
 * ------------------------------------------------------------------ */
(function () {
  if (window.__rastahCartDrawerAnalyticsInit) return;
  window.__rastahCartDrawerAnalyticsInit = true;

  function track(event, props) {
    var tmplMatch = String(document.body.className || '').match(/template-(\S+)/);
    var payload = Object.assign({
      event: event,
      ts: Date.now(),
      page_path: window.location.pathname + window.location.search,
      page_template: tmplMatch ? tmplMatch[1] : 'unknown'
    }, props || {});
    (window.dataLayer = window.dataLayer || []).push(payload);
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(event, payload);
      }
    } catch (_) { /* never let tracking break navigation */ }
  }

  /* ---- Cart state helpers ------------------------------------------ */
  // Last known cart payload (from /cart.js). Refreshed on cart:updated
  // and as a fallback if we can't read counts from the DOM.
  var lastCart = null;
  document.addEventListener('cart:updated', function (e) {
    if (e && e.detail && e.detail.cart) lastCart = e.detail.cart;
  });

  function readItemCount() {
    if (lastCart && typeof lastCart.item_count === 'number') return lastCart.item_count;
    var el = document.querySelector('[data-js-cart-count]');
    if (el) {
      var n = parseInt((el.textContent || '').trim(), 10);
      if (!isNaN(n)) return n;
    }
    var sup = document.querySelector('.quick-cart__heading sup');
    if (sup) {
      var n2 = parseInt((sup.textContent || '').trim(), 10);
      if (!isNaN(n2)) return n2;
    }
    return 0;
  }

  function readCartValue() {
    if (lastCart && typeof lastCart.total_price === 'number') {
      // /cart.js returns cents; preserve integer to keep payload compact
      // and unambiguous (consumers can divide by 100 themselves).
      return lastCart.total_price;
    }
    return null;
  }

  /* ---- Source / close-source hints --------------------------------- */
  var pendingOpenSource = '';
  var pendingCloseSource = '';
  var didCheckoutDuringSession = false;

  // Header cart icon click → open with source=header_cart_icon
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var icon = t.closest('[data-js-cart-icon]');
    if (icon) {
      pendingOpenSource = 'header_cart_icon';
      return;
    }
    // Checkout button click while drawer is open → close_source flag
    if (drawerOpen()) {
      var checkoutBtn = t.closest('[name="checkout"], .quick-cart__footer-checkout, [data-quick-cart-checkout]');
      if (checkoutBtn) {
        pendingCloseSource = 'checkout_button';
        didCheckoutDuringSession = true;
        return;
      }
      // "Continue shopping" link variants
      var cont = t.closest('[data-continue-shopping], .quick-cart__continue-shopping');
      if (cont) {
        pendingCloseSource = 'continue_shopping';
        return;
      }
      // X button
      var x = t.closest('[data-modal-close], .quick-cart__close-icon');
      if (x && t.closest('.quick-cart')) {
        pendingCloseSource = 'x_button';
        return;
      }
      // Scrim
      var scrim = t.closest('.quick-cart__overlay');
      if (scrim) {
        pendingCloseSource = 'scrim';
        return;
      }
    }
  }, true);

  // ATC form submit anywhere → open source = atc_button. Captured at
  // document level so any product form (PDP / quick-view) is covered.
  // The submit happens BEFORE the drawer opens, so we set the hint
  // and the MutationObserver picks it up when `.active` is added.
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || f.tagName !== 'FORM') return;
    if (f.matches('[data-product-form]') || f.action && f.action.indexOf('/cart/add') > -1) {
      pendingOpenSource = 'atc_button';
    }
  }, true);

  // The Stiletto cart-add path emits a document `cart:item-added`
  // event after the AJAX add succeeds and BEFORE quick-cart:open fires.
  // Cover this path too in case the form was submitted by a different
  // listener (e.g., header buy-now widgets).
  document.addEventListener('cart:item-added', function () {
    pendingOpenSource = pendingOpenSource || 'atc_button';
  });

  // Esc closes the drawer (Stiletto's focusTrap handles it). Capture.
  document.addEventListener('keydown', function (e) {
    if (!drawerOpen()) return;
    if (e.key === 'Escape' || e.keyCode === 27) {
      pendingCloseSource = 'esc';
    }
  }, true);

  /* ---- Drawer lifecycle observation -------------------------------- */
  var wrapper = null;
  var openedAt = 0;
  function drawerOpen() {
    return !!(wrapper && wrapper.classList.contains('active'));
  }

  function bindDrawer() {
    wrapper = document.querySelector('.quick-cart__wrapper');
    if (!wrapper) return false;
    var wasOpen = wrapper.classList.contains('active');

    var obs = new MutationObserver(function () {
      var nowOpen = wrapper.classList.contains('active');
      if (nowOpen === wasOpen) return;
      wasOpen = nowOpen;
      if (nowOpen) {
        openedAt = Date.now();
        didCheckoutDuringSession = false;
        track('cart_drawer_open', {
          source: pendingOpenSource || 'unknown',
          cart_item_count: readItemCount(),
          cart_value: readCartValue()
        });
        pendingOpenSource = '';
        pendingCloseSource = '';
      } else {
        if (openedAt) {
          track('cart_drawer_close', {
            dwell_ms: Date.now() - openedAt,
            close_source: pendingCloseSource || 'unknown',
            did_checkout: didCheckoutDuringSession
          });
        }
        openedAt = 0;
        pendingCloseSource = '';
      }
    });
    obs.observe(wrapper, { attributes: true, attributeFilter: ['class'] });
    return true;
  }

  /* ---- Boot --------------------------------------------------------- */
  function boot() {
    if (!bindDrawer()) {
      // Mounted from sections/quick-cart.liquid in layout — may hydrate
      // late on slow connections. Retry on load.
      window.addEventListener('load', bindDrawer, { once: true });
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
